/** Eksportene under «settings» i katalogen (docs/EKSPORT.md §9). API-halvdelen: `apps/api/src/export/definitions/settings/`. */
import type { ExportMeta } from '../types.js';

/**
 * MEDLEMMENE MED ROLLE OG STATUS: Kontakter › Organisasjon, med søket, «Aktive»/«Deaktiverte»,
 * rollefilteret og sorteringen fanen spør med, merkede rader og tabellens kolonner.
 */
const MEMBERS: ExportMeta = {
  id: 'settings.members',
  domain: 'settings',
  kind: 'table',
  titleKey: 'export.settings.members.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['selection', 'filter'],
  requires: ['membership:list'],
  guest: false,
  tableKey: 'organization-members',
  columns: [
    { key: 'displayName', labelKey: 'export.settings.members.columns.displayName', type: 'text' },
    { key: 'role', labelKey: 'export.settings.members.columns.role', type: 'status' },
    { key: 'status', labelKey: 'export.settings.members.columns.status', type: 'status' },
    { key: 'jobTitle', labelKey: 'export.settings.members.columns.jobTitle', type: 'text' },
    {
      key: 'responsibility',
      labelKey: 'export.settings.members.columns.responsibility',
      type: 'text',
    },
    { key: 'reportsTo', labelKey: 'export.settings.members.columns.reportsTo', type: 'text' },
    { key: 'joinedAt', labelKey: 'export.settings.members.columns.joinedAt', type: 'date' },
    {
      key: 'email',
      labelKey: 'export.settings.members.columns.email',
      type: 'email',
      defaultHidden: true,
    },
    {
      key: 'phone',
      labelKey: 'export.settings.members.columns.phone',
      type: 'phone',
      defaultHidden: true,
    },
    {
      key: 'deactivatedAt',
      labelKey: 'export.settings.members.columns.deactivatedAt',
      type: 'date',
      defaultHidden: true,
    },
  ],
};

/**
 * RETTIGHETER-MATRISEN: hver innstilling på Rettigheter som rad, rollene som kolonner, med standarden
 * og om organisasjonen har valgt noe annet. Bare det siden selv krever (`role_permission:list`).
 */
const PERMISSIONS: ExportMeta = {
  id: 'settings.permissions',
  domain: 'settings',
  kind: 'table',
  titleKey: 'export.settings.permissions.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['filter'],
  requires: ['role_permission:list'],
  guest: false,
  landscape: true,
  columns: [
    { key: 'section', labelKey: 'export.settings.permissions.columns.section', type: 'text' },
    { key: 'setting', labelKey: 'export.settings.permissions.columns.setting', type: 'text' },
    {
      key: 'superadmin',
      labelKey: 'export.settings.permissions.columns.superadmin',
      type: 'status',
    },
    { key: 'admin', labelKey: 'export.settings.permissions.columns.admin', type: 'status' },
    { key: 'member', labelKey: 'export.settings.permissions.columns.member', type: 'status' },
    { key: 'limited', labelKey: 'export.settings.permissions.columns.limited', type: 'status' },
    { key: 'standard', labelKey: 'export.settings.permissions.columns.standard', type: 'text' },
    { key: 'custom', labelKey: 'export.settings.permissions.columns.custom', type: 'boolean' },
  ],
};

/**
 * OVERSIKTEN «DELINGER» (sak 244.7): alt som er delt, med hvem, nivå, utløp og status — filteret
 * og de merkede som på Innstillinger › Delinger.
 */
const SHARES: ExportMeta = {
  id: 'settings.shares',
  domain: 'settings',
  kind: 'table',
  titleKey: 'export.settings.shares.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['selection', 'filter'],
  requires: ['share:list'],
  guest: false,
  landscape: true,
  columns: [
    { key: 'recipient', labelKey: 'export.settings.shares.columns.recipient', type: 'text' },
    { key: 'email', labelKey: 'export.settings.shares.columns.email', type: 'email' },
    { key: 'kind', labelKey: 'export.settings.shares.columns.kind', type: 'status' },
    { key: 'object', labelKey: 'export.settings.shares.columns.object', type: 'text' },
    { key: 'objectType', labelKey: 'export.settings.shares.columns.objectType', type: 'status' },
    { key: 'permission', labelKey: 'export.settings.shares.columns.permission', type: 'status' },
    { key: 'expiresAt', labelKey: 'export.settings.shares.columns.expiresAt', type: 'date' },
    { key: 'status', labelKey: 'export.settings.shares.columns.status', type: 'status' },
    { key: 'opened', labelKey: 'export.settings.shares.columns.opened', type: 'text' },
    { key: 'grantedBy', labelKey: 'export.settings.shares.columns.grantedBy', type: 'text' },
    {
      key: 'owner',
      labelKey: 'export.settings.shares.columns.owner',
      type: 'text',
      defaultHidden: true,
    },
    {
      key: 'createdAt',
      labelKey: 'export.settings.shares.columns.createdAt',
      type: 'date',
      defaultHidden: true,
    },
  ],
};

/**
 * AKTIVITETSLOGGEN FOR EN PERIODE: det `GET /v1/activity` gir (hvem, hva, på hvilket objekt, hvilke
 * felt — aldri verdiene), for `from`/`to` som et halvåpent vindu. Krever `audit_log:list`.
 */
const ACTIVITY: ExportMeta = {
  id: 'settings.activity',
  domain: 'settings',
  kind: 'table',
  titleKey: 'export.settings.activity.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['filter'],
  requires: ['audit_log:list'],
  guest: false,
  landscape: true,
  columns: [
    {
      key: 'occurredAt',
      labelKey: 'export.settings.activity.columns.occurredAt',
      type: 'datetime',
    },
    { key: 'actor', labelKey: 'export.settings.activity.columns.actor', type: 'text' },
    { key: 'event', labelKey: 'export.settings.activity.columns.event', type: 'text' },
    {
      key: 'changedFields',
      labelKey: 'export.settings.activity.columns.changedFields',
      type: 'text',
    },
    {
      key: 'action',
      labelKey: 'export.settings.activity.columns.action',
      type: 'status',
      defaultHidden: true,
    },
    {
      key: 'entityType',
      labelKey: 'export.settings.activity.columns.entityType',
      type: 'text',
      defaultHidden: true,
    },
    {
      key: 'entityLabel',
      labelKey: 'export.settings.activity.columns.entityLabel',
      type: 'text',
      defaultHidden: true,
    },
  ],
};

export const SETTINGS_EXPORTS: readonly ExportMeta[] = [MEMBERS, PERMISSIONS, SHARES, ACTIVITY];
